import React, { useEffect, useState } from 'react';
import {
  Box, MenuItem, Button, Fade,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CachedIcon from '@mui/icons-material/Cached';
import {
  lightBackgroundButtonStyles,
  cyanBlueBackgroundButtonStyles,
} from '../../../../styles/button';
import { AppDispatch, RootState } from '../../../../Redux/store';
import {
  getUserSession,
  revokeUserSession,
} from '../../../../Redux/Slices/Application/SessionManagement/sessionHandler';
import { SubLabel } from '../../../../styles/sublabel';
import { menuItemCSS, StyledSelect } from '../../../../styles/select';
import { SearchBar } from '../../../../styles/textField';
import { buttonIconStyles } from '../../../../styles/icon';
import CommonTable from '../../../../components/Table/CommonTable';
import DeleteModal from '../../../../components/modal/deleteModal';

function SessionManagement() {
  const { editScreen } = useSelector((state: RootState) => state.application);
  const { session } = useSelector((state: RootState) => state.session);
  const [openDialog, setOpenDialog] = useState(false);
  const flattenSessions = (sessionData: any[]) => sessionData.flatMap((group: any) => group.users.flatMap((user: any) => user.sessions.map((sessionItem: any) => ({
    identifier: group.identifier,
    email: user.email,
    uuid_key: user.uuid_key,
    session_key: sessionItem.session_key,
    browser: sessionItem.browser,
    session_validity: sessionItem.session_validity,
    login_timestamp: sessionItem.login_timestamp,
    status: sessionItem.status,
  }))));

  const flattenedSessions = flattenSessions(session);

  const dispatch = useDispatch<AppDispatch>();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBy, setSearchBy] = useState('group');
  const [filteredSession, setFilteredSession] = useState(flattenedSessions);
  const [selectedSessionKeys, setSelectedSessionKeys] = useState<string[]>([]);
  const [dropdownValue, setDropdownValue] = useState('none');

  useEffect(() => {
    dispatch(getUserSession());
  }, []);
  useEffect(() => {
    setFilteredSession(flattenSessions(session));
  }, [session]);
  const handleSearchByChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchBy(e.target.value);
  };

  const handleDropdownChange = (e: any) => {
    setDropdownValue(e.target.value);
  };
  const handleSearch = () => {
    let results = flattenSessions(session);

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      results = results.filter((sessionItem) => {
        if (searchBy === 'group') {
          return sessionItem.identifier.toLowerCase().includes(query);
        }
        if (searchBy === 'identifier') {
          return sessionItem.email.toLowerCase().includes(query);
        }
        return true;
      });
    }

    setFilteredSession(results);
  };

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const styles = {
    height: { xxl: '30.938rem', xl: '18.75rem', md: '18.75rem' },
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmRevoke = () => {
    dispatch(revokeUserSession(selectedSessionKeys));
    handleCloseDialog();
  };
  const handleCheckboxChange = (newSelectedRows: string[]) => {
    setSelectedSessionKeys(newSelectedRows);
  };

  return (
    <Fade
      in={editScreen === 'SessionManagement'}
      {...(editScreen === 'SessionManagement' ? { timeout: 1000 } : {})}
    >
      <Box>
        <Box display="flex" width="100%" justifyContent="space-between">
          <Box
            display="flex"
            gap="1rem"
            p="1rem"
            pt="1.5rem"
            alignItems="center"
          >
            <SearchBar
              placeholder="Search for session details"
              onChange={handleSearchQueryChange}
              sx={{
                maxWidth: {
                  xxl: '25rem',
                  xl: '20rem',
                },
              }}
            />
            <StyledSelect
              sx={{ width: '9rem' }}
              value={searchBy}
              onChange={handleSearchByChange}
            >
              <MenuItem value="group" sx={menuItemCSS}>
                <SubLabel>Group Name</SubLabel>
              </MenuItem>
              <MenuItem value="identifier" sx={menuItemCSS}>
                <SubLabel>Identifier</SubLabel>
              </MenuItem>
            </StyledSelect>
            <Button
              sx={{ ...lightBackgroundButtonStyles }}
              onClick={handleSearch}
            >
              Search
            </Button>
          </Box>
          <Box display="flex" gap="1rem" pt={2} alignItems="center">
            <StyledSelect
              sx={{ width: '10rem' }}
              value={dropdownValue}
              onChange={handleDropdownChange}
              displayEmpty
            >
              <MenuItem value="none" disabled sx={menuItemCSS}>
                <SubLabel>Select Action</SubLabel>
              </MenuItem>
              <MenuItem value="revoke" sx={menuItemCSS}>
                <SubLabel>Revoke Session</SubLabel>
              </MenuItem>
            </StyledSelect>
            <Button
              sx={{ ...cyanBlueBackgroundButtonStyles }}
              onClick={handleOpenDialog}
              disabled={
                dropdownValue === 'none' || selectedSessionKeys.length === 0
              }
            >
              Apply
            </Button>
            <Button
              size="small"
              startIcon={<CachedIcon sx={buttonIconStyles} />}
              onClick={() => dispatch(getUserSession())}
            >
              Reload
            </Button>
          </Box>
        </Box>
        <CommonTable
          data={filteredSession}
          columns={[
            { field: 'identifier', headerName: 'Group Name' },
            { field: 'email', headerName: 'User' },
            { field: 'session_key', headerName: 'Session Identifier' },
            { field: 'login_timestamp', headerName: 'Login Timestamp' },
            { field: 'browser', headerName: 'Browser' },
            { field: 'status', headerName: 'Status' },
            { field: 'session_validity', headerName: 'Session Validity' },
          ]}
          styles={styles}
          pages="Session Management"
          onSelectedRowsChange={handleCheckboxChange}
        />
        <DeleteModal
          open={openDialog}
          title="Confirm multiple sessions Deletion"
          message="Are you sure you want to delete the selected sessions ?"
          onConfirm={handleConfirmRevoke}
          onCancel={handleCloseDialog}
        />
      </Box>
    </Fade>
  );
}

export default SessionManagement;
